import { CNS_API_BASE } from "@/config";
import type {
  CNSOAuthPluginConnectionAttempt,
  CNSPluginCatalogPage,
  CNSPluginConnectionAttempt,
  CNSPluginConnectionOptionProjection,
  CNSPluginDetail,
  CNSPluginListQuery,
  CNSPutPluginEnablement,
  CNSStartPluginConnectionAttemptRequest,
  CNSWorkspacePluginCatalogPage,
  CNSSourceBrowseManifest,
  CNSWorkspacePluginDetail,
  CNSWorkspacePluginListQuery,
} from "@/models/plugin";
import { throwOnProblemResponse } from "@/types/problem-detail";

function buildListParams(query: CNSPluginListQuery = {}): URLSearchParams {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  for (const sort of query.sort ?? []) {
    params.append("sort", sort);
  }
  const trimmedQuery = query.query?.trim();
  if (trimmedQuery) params.set("query", trimmedQuery);
  const trimmedCategory = query.category?.trim();
  if (trimmedCategory) params.set("category", trimmedCategory);
  return params;
}

/** Lists preview and active plugin definitions from the catalog. */
export async function listPlugins(
  query: CNSPluginListQuery = {},
): Promise<CNSPluginCatalogPage> {
  const params = buildListParams(query);
  const suffix = params.size > 0 ? `?${params}` : "";
  const response = await fetch(`${CNS_API_BASE}/plugins${suffix}`, {
    credentials: "include",
  });
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load plugins");
  }
  return response.json();
}

/** Returns global plugin detail for a canonical key or declared alias. */
export async function getPlugin(pluginKey: string): Promise<CNSPluginDetail> {
  const response = await fetch(
    `${CNS_API_BASE}/plugins/${encodeURIComponent(pluginKey)}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load plugin");
  }
  return response.json();
}

/** Lists composed workspace plugin projections for the current user. */
export async function listWorkspacePlugins(
  workspaceId: string,
  query: CNSWorkspacePluginListQuery = {},
): Promise<CNSWorkspacePluginCatalogPage> {
  const params = buildListParams(query);
  if (query.projectId) params.set("projectId", query.projectId);
  if (query.scope) params.set("scope", query.scope);
  const suffix = params.size > 0 ? `?${params}` : "";
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins${suffix}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load workspace plugins");
  }
  return response.json();
}

/** Returns composed workspace plugin detail for the current user. */
export async function getWorkspacePlugin(
  workspaceId: string,
  pluginKey: string,
  projectId?: string | null,
): Promise<CNSWorkspacePluginDetail> {
  const params = new URLSearchParams();
  if (projectId) params.set("projectId", projectId);
  const suffix = params.size > 0 ? `?${params}` : "";
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins/${encodeURIComponent(pluginKey)}${suffix}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load workspace plugin");
  }
  return response.json();
}

/** Starts or replays a transport-neutral plugin connection attempt. */
export async function startPluginConnectionAttempt(
  workspaceId: string,
  pluginKey: string,
  connectionOptionKey: string,
  body: CNSStartPluginConnectionAttemptRequest,
  signal?: AbortSignal,
): Promise<CNSPluginConnectionAttempt> {
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins/${encodeURIComponent(pluginKey)}/connection-options/${encodeURIComponent(connectionOptionKey)}/connection-attempts`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to start plugin connection");
  }
  return response.json();
}

/** Resumes an owned plugin connection attempt without creating a replacement. */
export async function resumePluginConnectionAttempt(
  workspaceId: string,
  pluginKey: string,
  connectionOptionKey: string,
  attemptPublicId: string,
  signal?: AbortSignal,
): Promise<CNSPluginConnectionAttempt> {
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins/${encodeURIComponent(pluginKey)}/connection-options/${encodeURIComponent(connectionOptionKey)}/connection-attempts/${encodeURIComponent(attemptPublicId)}/resume`,
    {
      method: "POST",
      credentials: "include",
      signal,
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to resume plugin connection");
  }
  return response.json();
}

/** Cancels an owned plugin connection attempt. */
export async function cancelPluginConnectionAttempt(
  workspaceId: string,
  pluginKey: string,
  connectionOptionKey: string,
  attemptPublicId: string,
): Promise<void> {
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins/${encodeURIComponent(pluginKey)}/connection-options/${encodeURIComponent(connectionOptionKey)}/connection-attempts/${encodeURIComponent(attemptPublicId)}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to cancel plugin connection");
  }
}

/** Puts project-scoped plugin enablement for an owned credential. */
export async function putProjectPluginEnablement(
  projectId: string,
  pluginKey: string,
  connectionOptionKey: string,
  body: CNSPutPluginEnablement,
): Promise<CNSPluginConnectionOptionProjection> {
  const response = await fetch(
    `${CNS_API_BASE}/projects/${encodeURIComponent(projectId)}/plugins/${encodeURIComponent(pluginKey)}/connection-options/${encodeURIComponent(connectionOptionKey)}/enablement`,
    {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to update plugin enablement");
  }
  return response.json();
}

/** Disconnects the current user's owned credential for a connection option. */
export async function disconnectPluginConnection(
  workspaceId: string,
  pluginKey: string,
  connectionOptionKey: string,
): Promise<void> {
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins/${encodeURIComponent(pluginKey)}/connection-options/${encodeURIComponent(connectionOptionKey)}/connection`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to disconnect plugin");
  }
}

/** Lazily resolves the effective browse manifest for a plugin connection option. */
export async function fetchPluginBrowseManifest(
  workspaceId: string,
  pluginKey: string,
  connectionOptionKey: string,
  projectId: string,
): Promise<CNSSourceBrowseManifest> {
  const params = new URLSearchParams({ projectId });
  const response = await fetch(
    `${CNS_API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plugins/${encodeURIComponent(pluginKey)}/connection-options/${encodeURIComponent(connectionOptionKey)}/browse-manifest?${params}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    await throwOnProblemResponse(response, "Failed to load plugin browse access");
  }
  return response.json();
}

/** Narrows a plugin connection attempt to the MR6 OAuth branch. */
export function asOAuthPluginConnectionAttempt(
  attempt: CNSPluginConnectionAttempt,
): CNSOAuthPluginConnectionAttempt {
  if (attempt.kind !== "oauth") {
    throw new Error(`Unsupported plugin connection attempt kind: ${attempt.kind}`);
  }
  return attempt;
}
